<script setup>
import CFInvoicesSidebarItem from '#modules/invoices/components/CFInvoicesSidebarItem.vue';
import CFIcon from '@/ui/Icon/CFIcon.vue';
import { useInvoicesStore } from '#modules/invoices/stores/invoices.store.js';
import { storeToRefs } from 'pinia';
import CFContentPlaceholders from '@/shared/components/CFContentPlaceholders.vue';
import CFContentPlaceholdersBox from '@/shared/components/CFContentPlaceholdersBox.vue';
import CFIntersectionTrigger from '@/shared/components/CFIntersectionTrigger.vue';
import CFCircleSpinner from '@/shared/components/CFCircleSpinner.vue';
import CFButton from '@/ui/Button/CFButton.vue';
import { computed, ref, watch } from 'vue';
import axios from '@/app/api/axios.js';
import { useTeamStore } from '#modules/team/stores/team.store.js';
import dayjs from 'dayjs';
import CFWarningOldInvoices from '#modules/invoices/components/CFWarningOldInvoices.vue';
import { useRoute } from 'vue-router';
import { unexpectedError } from '@/shared/utils/unexpected-error.js';
import CFSidebar from '@/shared/components/CFSidebar.vue';
import { useInvoices } from '#modules/invoices/queries/useInvoices.js';
import CFInvoicesSearch from '#modules/invoices/components/CFInvoicesSearch.vue';

const route = useRoute();
const invoicesStore = useInvoicesStore();
const teamStore = useTeamStore();

const { team } = storeToRefs(teamStore);

const possiblyHasOldInvoices = computed(() => {
    return dayjs(team.value.attributes.created_at).isBefore(dayjs('2024-07-03'));
});

const { searchQuery, selectedInvoices, hasSelectedInvoices, hasReachedSelectedLimit } = storeToRefs(invoicesStore);

const { data: invoiceData, isPending, isLoading, isError, fetchNextPage, isFetchingNextPage } = useInvoices();
const invoices = computed(() => invoiceData.value?.pages.flatMap((page) => page.data) ?? []);

const isDownloadingBulkInvoices = ref(false);
const bulkDownloadInvoices = async () => {
    if (selectedInvoices.value.length === 0) {
        return;
    }

    isDownloadingBulkInvoices.value = true;

    try {
        const response = await axios.post(
            '/general/invoices/download-multiple',
            {
                ids: selectedInvoices.value,
            },
            {
                responseType: 'blob',
            }
        );

        const filename = response.headers
            .get('content-disposition')
            .split(';')
            .find((n) => n.includes('filename='))
            .replace('filename=', '')
            .replace(/"/g, '')
            .trim();

        const pdfBlob = new Blob([response.data], { type: response.data.type });
        const url = window.URL.createObjectURL(pdfBlob);
        const anchorElement = document.createElement('a');

        anchorElement.href = url;
        anchorElement.download = filename;

        document.body.appendChild(anchorElement);
        anchorElement.click();

        document.body.removeChild(anchorElement);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        unexpectedError(error);
    } finally {
        isDownloadingBulkInvoices.value = false;
    }
};

watch(
    () => route.query.number,
    (newValue) => {
        if (!newValue || newValue === searchQuery.value) {
            return;
        }

        searchQuery.value = newValue;
    },
    { immediate: true }
);
</script>

<template>
    <CFSidebar v-if="!isError">
        <div class="flex h-full flex-col overflow-x-hidden overflow-y-auto">
            <div class="bg-grey-light sticky top-0 z-10 px-6 pt-6 lg:pt-12">
                <h3 class="text-lg font-bold">{{ $t('general.invoices') }}</h3>

                <CFInvoicesSearch v-model="searchQuery" />
            </div>

            <div
                class="shrink-0 grow basis-0 px-6"
                :class="{
                    'pb-20': isPending,
                    'pb-32': hasSelectedInvoices,
                    'pb-8': !isPending && !hasSelectedInvoices,
                }"
            >
                <template v-if="!(isPending && !isLoading)">
                    <CFInvoicesSidebarItem
                        v-for="invoice in invoices"
                        :key="invoice.id"
                        v-model="selectedInvoices"
                        :invoice="invoice"
                        :active="route.params.invoice === invoice.id"
                        :disabled="hasReachedSelectedLimit && !selectedInvoices.includes(invoice.id)"
                        :on-click="(id) => invoicesStore.setActiveInvoice(id)"
                    />
                </template>

                <p
                    v-if="!isPending && invoices.length === 0"
                    class="mt-6 text-sm"
                >
                    {{ $t('invoices.no_invoices_found_for_query') }}
                </p>

                <CFWarningOldInvoices v-if="possiblyHasOldInvoices" />

                <CFIntersectionTrigger @intersected="fetchNextPage" />

                <CFContentPlaceholders v-if="isLoading && !isPending">
                    <CFContentPlaceholdersBox class="my-5 h-12" />
                    <hr class="border-neutrals-700" />
                    <CFContentPlaceholdersBox class="my-5 h-12" />
                </CFContentPlaceholders>

                <div
                    v-if="isFetchingNextPage && invoices.length > 0"
                    class="bg-grey-light absolute right-0 bottom-0 left-0 py-6"
                >
                    <CFCircleSpinner class="mx-auto size-6" />
                </div>
            </div>

            <Transition name="fade">
                <div
                    v-show="hasSelectedInvoices"
                    class="bg-neutrals-500 absolute right-0 bottom-0 left-0 flex h-24 items-center gap-3 px-[40px] py-4"
                >
                    <div class="flex items-center">
                        <CFIcon
                            name="download-bottom"
                            class="size-4"
                        />
                        <span class="ml-1 text-xs">
                            {{
                                $t('invoices.bulk_selection', {
                                    count: selectedInvoices.length,
                                    total: 3,
                                })
                            }}
                        </span>
                    </div>
                    <CFButton
                        size="sm"
                        variant="outline"
                        :loading="isDownloadingBulkInvoices"
                        @click="bulkDownloadInvoices"
                    >
                        {{ $t('invoices.bulk_download') }}
                    </CFButton>
                </div>
            </Transition>
        </div>
    </CFSidebar>
</template>
